import { UTCDate } from '@date-fns/utc';
import { CsvColumnConfig, CsvRow } from '../types/csv-download-config';

/**
 * Generates CSV content from data and column configuration.
 *
 * @param data - Array of data to export
 * @param columns - Column configuration defining how to extract and format data
 * @param dataTransformer - Optional custom data transformer function
 * @returns CSV content as a string
 * @throws Error if data is invalid or CSV generation fails
 */
export const generateCsv = <TData>(
  data: TData[],
  columns: CsvColumnConfig<TData>[],
  dataTransformer?: (data: TData[]) => CsvRow[],
): string => {
  try {
    // Validate inputs
    if (!Array.isArray(data)) {
      throw new Error('Data must be an array');
    }

    if (!Array.isArray(columns) || columns.length === 0) {
      throw new Error('Columns configuration must be a non-empty array');
    }

    const csvRows = dataTransformer ? dataTransformer(data) : transformDataToCsvRows(data, columns);
    const csvContent = generateCsvContent(csvRows, columns);
    return csvContent;
  } catch (error) {
    throw new Error(`Failed to generate CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Transforms data to CSV rows using column configuration.
 *
 * @param data - Array of data to transform
 * @param columns - Column configuration defining how to extract and format data
 * @returns Array of CSV rows
 */
export const transformDataToCsvRows = <TData>(data: TData[], columns: CsvColumnConfig<TData>[]): CsvRow[] => {
  return data.map((row) => {
    const csvRow: CsvRow = {};

    columns.forEach((column) => {
      const value = column.accessor(row);
      const formattedValue = column.formatter ? column.formatter(value) : String(value ?? '');
      csvRow[column.key] = formattedValue;
    });

    return csvRow;
  });
};

const generateCsvContent = <TData>(rows: CsvRow[], columns: CsvColumnConfig<TData>[]): string => {
  // Generate header row
  const headers = columns.map((column) => column.header);
  const headerRow = headers.join(',');

  // Generate data rows
  const dataRows = rows.map((row) => {
    return columns
      .map((column) => {
        // Try to get value by column key first, then by header if key doesn't exist
        let value = row[column.key];
        if (value === undefined) {
          // For custom data transformers, try to get value by header
          value = row[column.header];
        }
        // Escape CSV values properly
        return escapeCsvValue(String(value || ''));
      })
      .join(',');
  });

  // Combine header and data rows
  return [headerRow, ...dataRows].join('\n');
};

const escapeCsvValue = (value: string): string => {
  // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

export const downloadCsv = (csvContent: string, filename: string): void => {
  // Create blob with CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const generateDefaultFilename = (prefix: string = 'data'): string => {
  const date = new UTCDate().toISOString().split('T')[0]; // YYYY-MM-DD format
  return `${prefix}-${date}.csv`;
};
